import { Box, Theme, Typography } from "@outschool/backpack";
import { ActivityListing_ListingFragment } from "@outschool/gql-frontend-generated";
import { measurePerformance, useProductClicked } from "@outschool/ui-analytics";
import {
  SectionRibbon,
  UserHeadshotImage
} from "@outschool/ui-components-shared";
import { SimpleRatings } from "@outschool/ui-components-website";
import { TrackingProperties } from "@outschool/ui-legacy-component-library";
import { useLinkComponent, useNavigation } from "@outschool/ui-utils";
import React from "react";

import { SaveHeart } from "../saving/SaveButton";
import ListingDetails from "../search/ListingDetails";
import { ActivityListingProps } from "./ActivityListing";
import ActivityListingPhoto from "./components/ActivityListingPhoto";
import { NextStartTime } from "./NextStartTime";

type DesktopListingProps = {
  activity: ActivityListing_ListingFragment["activity"];
  leader: ActivityListing_ListingFragment["leader"];
  insertionId?: ActivityListing_ListingFragment["insertionId"];
  isActivitySavedByCurrentUser: boolean;
  trackingProperties: TrackingProperties;
  disabled?: boolean;
  openInNewTab: boolean;
  listingIndex?: number;
  pageLocation: string;
  variant?: ActivityListingProps["variant"];
  openInSSRForLoggedOutUsers?: boolean;
  nextSection: ActivityListing_ListingFragment["nextSection"];
  listingSectionOverride?: React.ReactNode;
  showCredits?: boolean;
  showFundingBadge?: boolean;
};

function useLinkPath<T>({ activity }: T & { activity: { path: string } }) {
  const [link, setLink] = React.useState(activity.path);

  React.useEffect(() => {
    const { search } = document.location;
    const learnerPlanGoalUid = new URLSearchParams(search).get(
      "learnerPlanGoalUid"
    );

    if (learnerPlanGoalUid) {
      setLink(activity.path + `?learnerPlanGoalUid=${learnerPlanGoalUid}`);
    }
  }, [activity.path]);

  return link;
}

export const DesktopListing = ({
  activity,
  nextSection,
  leader,
  variant,
  isActivitySavedByCurrentUser,
  trackingProperties,
  openInNewTab,
  insertionId,
  listingIndex,
  disabled = false,
  openInSSRForLoggedOutUsers = false,

  listingSectionOverride,
  showCredits,
  showFundingBadge
}: DesktopListingProps) => {
  const Link = useLinkComponent();
  const navigate = useNavigation();
  const trackProductClicked = useProductClicked();

  const onLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    trackProductClicked({ activity, leader }, listingIndex);

    if (openInSSRForLoggedOutUsers) {
      e.preventDefault();
      // Hard navigate so a full page load opens SSR.
      navigate(activity?.path, { hardNav: true, newTab: openInNewTab });
    }
  };

  const linkPath = useLinkPath({ activity });

  const LinkComponent = React.useMemo(
    () =>
      ({ children }: { children: React.ReactNode }) => {
        const sharedProperties: Record<string, string> = insertionId
          ? {
              ...trackingProperties,
              activity_uid: activity?.uid,
              insertion_id: insertionId
            }
          : {
              ...trackingProperties,
              activity_uid: activity?.uid
            };

        return (
          <Link
            to={linkPath}
            trackingName={"Activity Impression"}
            sharedProperties={sharedProperties}
            trackingTouchEventName="Activity Impression Click"
            trackingViewEventName="Activity Impression"
            trackingUniqueId={activity?.uid}
            disabled={disabled}
            tabIndex={disabled ? -1 : undefined}
            useLinkTracking
            onClick={onLinkClick}
            target={openInNewTab ? "_blank" : undefined}
            rel={openInNewTab ? "noopener noreferrer" : undefined}
            style={{ textDecoration: "none" }}
            trackViews={true}
          >
            {children}
          </Link>
        );
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <LinkComponent>
      <Box
        flex
        sx={{
          borderStyle: "solid",
          borderWidth: "1px",
          borderRadius: "1em",
          borderColor: "grey.200",
          margin: "0 1em 2em 1em",
          "&:hover": {
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.25)"
          }
        }}
      >
        <Box
          flex
          sx={{
            marginTop: "auto",
            whiteSpace: "nowrap",
            position: "relative",
            top: "-1.5rem"
          }}
        >
          <SectionRibbon
            section={nextSection}
            activity={activity}
            sx={{
              left: "-1.5em",
              zIndex: 1
            }}
          />
        </Box>
        <Box
          flex
          sx={{
            padding: "1em 0 1em 1em",
            color: "grey.900",
            flex: 3
          }}
        >
          <Box
            flex
            sx={(theme: Theme) => ({
              ...(variant === "Share"
                ? {
                    minWidth: "225px",
                    maxWidth: "225px",
                    [theme.breakpoints.up("lg")]: {
                      minWidth: "200px",
                      maxWidth: "200px"
                    },
                    [theme.breakpoints.up("xl")]: {
                      minWidth: "250px",
                      maxWidth: "250px"
                    },
                    alignItems: "center"
                  }
                : { width: 350 })
            })}
          >
            <ActivityListingPhoto
              activity={activity}
              useOriginalAspectRatio
              onLoad={
                listingIndex === 0
                  ? () => {
                      measurePerformance("os-page-load", {
                        source: "image",
                        variant
                      });
                    }
                  : undefined
              }
            />
          </Box>

          <Box
            flex
            sx={{
              maxWidth: "430px",
              marginLeft: "16px",
              flexDirection: "column",
              gap: "10px"
            }}
          >
            <Box flex>
              <Box
                flex
                sx={{
                  padding: "12px",
                  justifyContent: "center"
                }}
              >
                <SaveHeart
                  isSet={isActivitySavedByCurrentUser}
                  activity={activity}
                  // @ts-ignore TS(2322): Type '{ __typename?: "Leader" | undefined; uid: st... Remove this comment to see the full error message
                  leader={leader}
                  trackingProperties={trackingProperties}
                  disabled={disabled}
                  unfilledColor={"error.main"}
                  sx={{ fontSize: "24px" }}
                />
              </Box>
              <Typography
                data-test-id="activity-title"
                variant="h6"
                emphasized
                sx={{
                  overflow: "hidden",
                  wordBreak: "break-word",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  alignSelf: "center"
                }}
              >
                {activity.titleTranslated}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                wordBreak: "break-word",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                color: "grey.600",
                marginLeft: "48px"
              }}
            >
              {activity.summaryTranslated || ""}
            </Typography>
            <NextStartTime
              nextSectionStartTime={nextSection?.start_time}
              nextOngoingMeetingStartTime={
                nextSection?.nextOngoingMeeting?.start_time
              }
            />
            <Box flex sx={{ marginTop: "auto" }}>
              <Box flex sx={{ padding: "4px" }}>
                <UserHeadshotImage
                  user={leader}
                  size={40}
                  sx={{ border: "1px solid", borderColor: "gray200" }}
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "grey.600", paddingBottom: "10px" }}
                >
                  {leader.name}
                </Typography>
                <SimpleRatings
                  starRating={leader.averageActivityStarRating}
                  reviewCount={leader.reviewCount}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {variant === "Review" && <>{listingSectionOverride}</>}
        {variant !== "Review" && (
          <Box
            flex
            sx={{
              flex: "1",
              color: "grey.600",
              maxWidth: "250px",
              marginLeft: "auto"
            }}
          >
            <ListingDetails
              // @ts-ignore TS(2322): Type '{ __typename?: "Activity" | undefined; uid: ... Remove this comment to see the full error message
              activity={activity}
              variant={variant}
              showCredits={showCredits}
              showFundingBadge={showFundingBadge}
            />
          </Box>
        )}
      </Box>
    </LinkComponent>
  );
};
